/**
 * Network event capture (Phase 1). Patches `window.fetch` and
 * `XMLHttpRequest` to surface HTTP activity that's invisible to a DOM-only
 * agent: 4xx/5xx, 401 redirects, 429 rate limits, slow APIs, captcha
 * challenges, offline. The DOM doesn't change on a 401 → login redirect or
 * a hung POST, but the visitor sees a frozen UI; the agent must too.
 *
 * Design constraints:
 *   - Run inside the customer's iframe, on a real production app. Patching
 *     fetch/XHR must NEVER break the host page. All wrappers are wrapped in
 *     try/catch and degrade silently. If patching itself fails (rare CSP
 *     trick, missing `fetch`), we emit a single `network-shim-disabled`
 *     signal and the agent narrates conservatively.
 *   - No request bodies are captured. URLs lose query *values*, keep keys.
 *     Response bodies are only sampled on non-2xx (first 256 chars, with
 *     `password|token|secret|email|api_key|authorization` keys masked).
 *   - Ring buffer caps the memory footprint. The server reads the last
 *     N-since-watermark each turn and computes a derived `network_status`.
 *
 * This module does NOT decide what to do with the events — that's the
 * server's job (LIVE NETWORK block in prompt-builder, plus the
 * `re_auth_demo_account` and `await_visitor_captcha` tools). Keep this file
 * a pure observer.
 */

/** Single captured network event. Wire-shape; do not change without server. */
export interface NetworkEvent {
  /** Monotonic id, used as a watermark by the server. */
  id: number;
  /** Wall-clock ms when the request started. */
  ts: number;
  /** Upper-cased HTTP method. */
  method: string;
  /**
   * URL path + truncated query (keys preserved, values redacted to `*`).
   * Origin is dropped if same-origin to keep the payload small; cross-origin
   * keeps the host so the agent can identify third-party calls (Stripe, etc).
   */
  url: string;
  /** HTTP status. 0 = network error or aborted. */
  status: number;
  /** Round-trip ms from request start to response (or error). */
  latencyMs: number;
  /** Approximate response size in bytes (Content-Length when known). */
  responseSize?: number;
  /**
   * Redacted body excerpt (first 256 chars). Only set on non-2xx responses
   * and only if the response was JSON or text. Sensitive keys are masked
   * before storage.
   */
  bodyExcerpt?: string;
  /**
   * `Retry-After` header value in ms when present (parsed from seconds or
   * HTTP-date). Used by the server's 429 handler to throttle the next turn.
   */
  retryAfterMs?: number;
  /**
   * `true` if the response is a redirect to a known login route (heuristic:
   * 30x to `/login`, `/sign-in`, `/auth/*`, or current location.href changed
   * to one of these within 250ms of the response). Tells the server to fire
   * the auth-expired escalation.
   */
  redirectedToLogin?: boolean;
  /** Initiator hint — what kind of request this was. Used for filtering. */
  initiator: 'fetch' | 'xhr' | 'beacon';
}

/** Ambient (non-request) signals the shim emits alongside per-request events. */
export interface NetworkAmbientSignal {
  kind:
    | 'shim-disabled'        // patching failed; agent should fall back
    | 'offline'              // navigator.onLine === false
    | 'online'               // came back online
    | 'captcha-detected'     // reCAPTCHA / hCaptcha / Turnstile iframe mounted
    | 'captcha-cleared'      // captcha iframe removed from DOM
    | 'pending-stuck';       // an in-flight request is older than SLOW_THRESHOLD
  ts: number;
  detail?: Record<string, unknown>;
}

/** Derived per-snapshot network status. The server prompts off this. */
export type NetworkStatus =
  | 'idle'
  | 'pending'        // ≥1 request in-flight
  | 'failed'         // last event was a 4xx/5xx (excluding rate_limited / auth_expired which take precedence)
  | 'rate_limited'   // last event was 429
  | 'auth_expired'   // last event was 401 OR redirected_to_login
  | 'offline'
  | 'captcha_pending'
  | 'disabled';      // shim could not install

const RING_CAP = 30;
const AMBIENT_RING_CAP = 10;
const SLOW_THRESHOLD_MS = 2_000;
const URL_MAX_LEN = 240;
const BODY_EXCERPT_MAX = 256;
const SENSITIVE_KEY_RE =
  /("?(password|passwd|secret|token|access[_-]?token|refresh[_-]?token|api[_-]?key|authorization|email|cookie|session)"?\s*[:=]\s*)("[^"]*"|[^,}\s]+)/gi;
const LOGIN_PATH_RE = /^\/(login|signin|sign-in|sign_in|auth(\/(login|signin)|$))/i;
const CAPTCHA_HOST_RE = /(google\.com\/recaptcha|hcaptcha\.com|challenges\.cloudflare\.com|cdn\.turnstile)/i;

let installed = false;
let nextId = 1;
let events: NetworkEvent[] = [];
let ambient: NetworkAmbientSignal[] = [];
let inflight = new Map<
  number,
  { startedAt: number; method: string; url: string; initiator: NetworkEvent['initiator'] }
>();
let pendingWatchdog: ReturnType<typeof setInterval> | null = null;
let captchaObserver: MutationObserver | null = null;
let onlineHandler: (() => void) | null = null;
let offlineHandler: (() => void) | null = null;

/**
 * Install the shim. Idempotent — calling twice is a no-op. Returns `true`
 * on success, `false` if patching failed (caller should skip Phase 1
 * surfacing and emit `disabled` to the prompt).
 */
export function installNetworkShim(): boolean {
  if (installed) return true;
  let ok = true;
  try {
    patchFetch();
  } catch {
    ok = false;
  }
  try {
    patchXHR();
  } catch {
    ok = false;
  }
  try {
    setupCaptchaWatcher();
  } catch {
    /* non-fatal */
  }
  try {
    setupOnlineWatcher();
  } catch {
    /* non-fatal */
  }
  try {
    setupPendingWatchdog();
  } catch {
    /* non-fatal */
  }
  installed = true;
  if (!ok) pushAmbient({ kind: 'shim-disabled', ts: Date.now() });
  return ok;
}

/**
 * Drain events newer than `sinceId` (exclusive). Up to `limit` returned in
 * insertion order. The watermark is per-server-snapshot — the server stores
 * the last id it saw, sends it back, and we slice from there. The buffer is
 * NOT cleared; old events age out via the ring cap.
 */
export function drainNetworkEventsSince(sinceId: number, limit = 5): NetworkEvent[] {
  if (events.length === 0) return [];
  const filtered = sinceId > 0 ? events.filter((e) => e.id > sinceId) : events;
  return filtered.slice(-limit);
}

export function getLastNetworkEventId(): number {
  return events.length === 0 ? 0 : events[events.length - 1].id;
}

export function drainAmbientSignals(): NetworkAmbientSignal[] {
  if (ambient.length === 0) return [];
  const out = ambient.slice();
  ambient = [];
  return out;
}

/**
 * Compute the derived status from the most recent events + ambient signals.
 * Order of precedence: shim-disabled > offline > captcha > auth > rate
 * limit > failed > pending > idle. The server uses this to gate prompt
 * blocks and tools.
 */
export function computeNetworkStatus(): NetworkStatus {
  if (!installed) return 'disabled';
  const recentAmbient = ambient.slice(-AMBIENT_RING_CAP);
  if (recentAmbient.some((a) => a.kind === 'shim-disabled')) return 'disabled';
  // Online/offline transitions: latest wins.
  let isOffline = false;
  for (const a of recentAmbient) {
    if (a.kind === 'offline') isOffline = true;
    else if (a.kind === 'online') isOffline = false;
  }
  if (isOffline) return 'offline';
  // Captcha: latest detected/cleared wins.
  let captchaPending = false;
  for (const a of recentAmbient) {
    if (a.kind === 'captcha-detected') captchaPending = true;
    else if (a.kind === 'captcha-cleared') captchaPending = false;
  }
  if (captchaPending) return 'captcha_pending';

  const last = events[events.length - 1];
  if (last) {
    if (last.status === 401 || last.redirectedToLogin) return 'auth_expired';
    if (last.status === 429) return 'rate_limited';
    if (last.status >= 400 && last.status < 600) return 'failed';
  }
  if (inflight.size > 0) return 'pending';
  return 'idle';
}

/**
 * Cluster D item 34 — wait until the network is idle (no in-flight fetch /
 * XHR for `idleMs` consecutive milliseconds) or `timeoutMs` elapses. Replaces
 * fixed 200ms post-nav waits in iframe-agent.captureObservationAfterAction.
 *
 * Returns true when idle, false when we timed out with requests still in
 * flight. Callers proceed either way; the boolean is just for telemetry.
 */
export async function waitForNetworkIdle(idleMs = 300, timeoutMs = 4000): Promise<boolean> {
  const deadline = Date.now() + timeoutMs;
  let idleSince = inflight.size === 0 ? Date.now() : null;
  while (Date.now() < deadline) {
    if (inflight.size === 0) {
      if (idleSince === null) idleSince = Date.now();
      if (Date.now() - idleSince >= idleMs) return true;
    } else {
      idleSince = null;
    }
    await new Promise((r) => setTimeout(r, 60));
  }
  return inflight.size === 0;
}

/** For tests / mode switches. Clears all internal state but does not unhook patches. */
export function resetNetworkEvents(): void {
  nextId = 1;
  events = [];
  ambient = [];
  inflight.clear();
}

/** Fully tear down — only used in non-iframe contexts. */
export function uninstallNetworkShim(): void {
  if (pendingWatchdog) {
    clearInterval(pendingWatchdog);
    pendingWatchdog = null;
  }
  if (captchaObserver) {
    captchaObserver.disconnect();
    captchaObserver = null;
  }
  if (onlineHandler) {
    window.removeEventListener('online', onlineHandler);
    onlineHandler = null;
  }
  if (offlineHandler) {
    window.removeEventListener('offline', offlineHandler);
    offlineHandler = null;
  }
  installed = false;
}

// ─── Internals ────────────────────────────────────────────────────────────

function pushEvent(e: NetworkEvent): void {
  events.push(e);
  if (events.length > RING_CAP) events.splice(0, events.length - RING_CAP);
}

function pushAmbient(s: NetworkAmbientSignal): void {
  ambient.push(s);
  if (ambient.length > AMBIENT_RING_CAP) ambient.splice(0, ambient.length - AMBIENT_RING_CAP);
}

function patchFetch(): void {
  if (typeof window.fetch !== 'function') return;
  const orig = window.fetch.bind(window);
  window.fetch = (...args: Parameters<typeof fetch>): Promise<Response> => {
    const id = nextId++;
    const startedAt = Date.now();
    const { method, url } = parseFetchArgs(args);
    inflight.set(id, { startedAt, method, url, initiator: 'fetch' });
    return orig(...args)
      .then((res) => {
        recordResponse({
          id,
          startedAt,
          method,
          url,
          initiator: 'fetch',
          status: res.status,
          headers: res.headers,
          // Don't read the body for 2xx — risk of breaking streaming responses
          // (the customer may be consuming a streamed response themselves).
          // Only sample on non-2xx where there's a fix-it message we want.
          tryReadBody: !res.ok,
          response: res,
        });
        return res;
      })
      .catch((err) => {
        recordError({ id, startedAt, method, url, initiator: 'fetch', err });
        throw err;
      })
      .finally(() => {
        inflight.delete(id);
      });
  };
}

function patchXHR(): void {
  if (typeof XMLHttpRequest === 'undefined') return;
  const origOpen = XMLHttpRequest.prototype.open;
  const origSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (
    this: XMLHttpRequest & { __iraaCtx?: { method: string; url: string } },
    method: string,
    url: string | URL,
    ...rest: unknown[]
  ) {
    this.__iraaCtx = { method: (method || 'GET').toUpperCase(), url: stringifyUrl(url) };
    // eslint-disable-next-line prefer-rest-params
    return origOpen.apply(this, arguments as unknown as Parameters<typeof origOpen>);
  } as typeof XMLHttpRequest.prototype.open;

  XMLHttpRequest.prototype.send = function (
    this: XMLHttpRequest & { __iraaCtx?: { method: string; url: string } },
    ...args: unknown[]
  ) {
    const ctx = this.__iraaCtx;
    if (ctx) {
      const id = nextId++;
      const startedAt = Date.now();
      const { method, url } = ctx;
      inflight.set(id, { startedAt, method, url: redactUrl(url), initiator: 'xhr' });
      const onSettle = (): void => {
        inflight.delete(id);
        try {
          if (this.readyState !== 4) return;
          if (this.status === 0) {
            recordError({ id, startedAt, method, url, initiator: 'xhr', err: new Error('xhr_status_0') });
            return;
          }
          const headers = parseXhrHeaders(this);
          const responseSize = headerToInt(headers.get('content-length'));
          const ev: NetworkEvent = {
            id,
            ts: startedAt,
            method,
            url: redactUrl(url),
            status: this.status,
            latencyMs: Date.now() - startedAt,
            responseSize,
            initiator: 'xhr',
          };
          if (this.status === 429) {
            const ra = headers.get('retry-after');
            if (ra) ev.retryAfterMs = parseRetryAfter(ra);
          }
          if (this.status >= 400 && this.status < 600) {
            try {
              const text = (this.responseType === '' || this.responseType === 'text') ? this.responseText : '';
              if (text) ev.bodyExcerpt = redactBody(text);
            } catch { /* responseType !== text */ }
          }
          if (isLoginRedirect(this.status, headers, url)) ev.redirectedToLogin = true;
          pushEvent(ev);
        } catch { /* swallow */ }
      };
      this.addEventListener('loadend', onSettle, { once: true });
    }
    // eslint-disable-next-line prefer-rest-params
    return origSend.apply(this, arguments as unknown as Parameters<typeof origSend>);
  } as typeof XMLHttpRequest.prototype.send;
}

function recordResponse(args: {
  id: number;
  startedAt: number;
  method: string;
  url: string;
  initiator: NetworkEvent['initiator'];
  status: number;
  headers: Headers;
  tryReadBody: boolean;
  response: Response;
}): void {
  const ev: NetworkEvent = {
    id: args.id,
    ts: args.startedAt,
    method: args.method,
    url: redactUrl(args.url),
    status: args.status,
    latencyMs: Date.now() - args.startedAt,
    responseSize: headerToInt(args.headers.get('content-length')),
    initiator: args.initiator,
  };
  if (args.status === 429) {
    const ra = args.headers.get('retry-after');
    if (ra) ev.retryAfterMs = parseRetryAfter(ra);
  }
  if (isLoginRedirect(args.status, args.headers, args.url)) ev.redirectedToLogin = true;
  if (args.tryReadBody) {
    // Clone so the customer's code can still read the body — required by the
    // Fetch spec; reading our copy doesn't disturb theirs.
    args.response
      .clone()
      .text()
      .then((text) => {
        if (text) ev.bodyExcerpt = redactBody(text);
        pushEvent(ev);
      })
      .catch(() => pushEvent(ev));
  } else {
    pushEvent(ev);
  }
}

function recordError(args: {
  id: number;
  startedAt: number;
  method: string;
  url: string;
  initiator: NetworkEvent['initiator'];
  err: unknown;
}): void {
  pushEvent({
    id: args.id,
    ts: args.startedAt,
    method: args.method,
    url: redactUrl(args.url),
    status: 0,
    latencyMs: Date.now() - args.startedAt,
    initiator: args.initiator,
    bodyExcerpt: errorMessage(args.err).slice(0, 80),
  });
}

function parseFetchArgs(args: Parameters<typeof fetch>): { method: string; url: string } {
  const [input, init] = args;
  let method = (init?.method ?? 'GET').toString().toUpperCase();
  let url: string;
  if (typeof input === 'string') {
    url = input;
  } else if (input instanceof URL) {
    url = input.toString();
  } else if (input instanceof Request) {
    url = input.url;
    if (!init?.method) method = input.method.toUpperCase();
  } else {
    url = String(input);
  }
  return { method, url };
}

function stringifyUrl(u: string | URL): string {
  return typeof u === 'string' ? u : u.toString();
}

/** Strip query VALUES (keep keys). Same-origin → just the path; cross-origin → host + path. */
function redactUrl(raw: string): string {
  let u: URL;
  try {
    u = new URL(raw, location.href);
  } catch {
    return raw.slice(0, URL_MAX_LEN);
  }
  const sameOrigin = u.origin === location.origin;
  const params: string[] = [];
  u.searchParams.forEach((_v, k) => params.push(`${k}=*`));
  const query = params.length > 0 ? `?${params.join('&')}` : '';
  const head = sameOrigin ? u.pathname : `${u.host}${u.pathname}`;
  const out = `${head}${query}`;
  return out.length > URL_MAX_LEN ? out.slice(0, URL_MAX_LEN - 1) + '…' : out;
}

function redactBody(raw: string): string {
  const trimmed = raw.length > BODY_EXCERPT_MAX * 2 ? raw.slice(0, BODY_EXCERPT_MAX * 2) : raw;
  const masked = trimmed.replace(SENSITIVE_KEY_RE, (_, lhs) => `${lhs}"***"`);
  return masked.length > BODY_EXCERPT_MAX ? masked.slice(0, BODY_EXCERPT_MAX) : masked;
}

function headerToInt(v: string | null): number | undefined {
  if (!v) return undefined;
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

function parseRetryAfter(raw: string): number | undefined {
  const seconds = parseInt(raw, 10);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1000;
  const date = Date.parse(raw);
  if (!Number.isNaN(date)) return Math.max(0, date - Date.now());
  return undefined;
}

function parseXhrHeaders(xhr: XMLHttpRequest): Headers {
  const h = new Headers();
  try {
    const raw = xhr.getAllResponseHeaders();
    raw.split(/\r?\n/).forEach((line) => {
      const idx = line.indexOf(':');
      if (idx > 0) {
        const k = line.slice(0, idx).trim();
        const v = line.slice(idx + 1).trim();
        if (k && v) h.set(k, v);
      }
    });
  } catch { /* ignore */ }
  return h;
}

function isLoginRedirect(status: number, headers: Headers, originalUrl: string): boolean {
  if (status >= 300 && status < 400) {
    const loc = headers.get('location');
    if (loc) {
      try {
        const u = new URL(loc, originalUrl);
        if (LOGIN_PATH_RE.test(u.pathname)) return true;
      } catch { /* ignore */ }
    }
  }
  return false;
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  try { return String(err); } catch { return 'unknown'; }
}

function setupCaptchaWatcher(): void {
  // Initial scan — captcha may already be on screen.
  const scan = (): boolean => {
    const iframes = document.querySelectorAll('iframe');
    for (let i = 0; i < iframes.length; i++) {
      const src = iframes[i].getAttribute('src') ?? '';
      if (CAPTCHA_HOST_RE.test(src)) return true;
    }
    return false;
  };
  let captchaPresent = scan();
  if (captchaPresent) pushAmbient({ kind: 'captcha-detected', ts: Date.now() });

  if (typeof MutationObserver === 'undefined') return;
  captchaObserver = new MutationObserver(() => {
    const present = scan();
    if (present && !captchaPresent) {
      captchaPresent = true;
      pushAmbient({ kind: 'captcha-detected', ts: Date.now() });
    } else if (!present && captchaPresent) {
      captchaPresent = false;
      pushAmbient({ kind: 'captcha-cleared', ts: Date.now() });
    }
  });
  try {
    captchaObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['src'] });
  } catch { /* document.body not yet available */ }
}

function setupOnlineWatcher(): void {
  if (typeof navigator === 'undefined' || typeof window.addEventListener !== 'function') return;
  if (navigator.onLine === false) pushAmbient({ kind: 'offline', ts: Date.now() });
  onlineHandler = () => pushAmbient({ kind: 'online', ts: Date.now() });
  offlineHandler = () => pushAmbient({ kind: 'offline', ts: Date.now() });
  window.addEventListener('online', onlineHandler);
  window.addEventListener('offline', offlineHandler);
}

function setupPendingWatchdog(): void {
  pendingWatchdog = setInterval(() => {
    if (inflight.size === 0) return;
    const now = Date.now();
    let stuck: { url: string; method: string; ageMs: number } | null = null;
    inflight.forEach((v) => {
      const age = now - v.startedAt;
      if (age >= SLOW_THRESHOLD_MS) {
        if (!stuck || age > stuck.ageMs) stuck = { url: v.url, method: v.method, ageMs: age };
      }
    });
    if (stuck) {
      // Coalesce: only emit once per stuck-window so we don't spam.
      const last = ambient[ambient.length - 1];
      if (!last || last.kind !== 'pending-stuck' || now - last.ts > SLOW_THRESHOLD_MS) {
        pushAmbient({ kind: 'pending-stuck', ts: now, detail: stuck as unknown as Record<string, unknown> });
      }
    }
  }, 1_000);
}
